import { environment } from "@/config/env";
import axios from "axios";
import {
  BadgeCheckIcon,
  CakeIcon,
  Globe,
  MailIcon,
  Phone,
  UserRoundCheck,
  UserRoundPlus,
  UserRoundSearch,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingItem from "./LoadingItem";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Item, ItemDescription, ItemMedia, ItemTitle } from "./ui/item";
import {
  RadioRows,
  RadioRowsItem,
  RadioRowsItemDescription,
  RadioRowsItemTitle,
} from "./ui/radio-rows";
import { Spinner } from "./ui/spinner";

// Interfaccia per i dati degli utenti dal PMS
export interface PMSUser {
  birthCountry?: string;
  birthDate?: Date;
  birthPlace?: string;
  birthProvince?: string;
  email?: string;
  firstname?: string;
  fiscalCode?: string;
  gender?: string;
  idGuest: string;
  idPms?: string;
  lastname?: string;
  phone?: string;
  postalCode?: string;
  residenceAddress?: string;
  residenceCity?: string;
  residenceProvince?: string;
  residenceState?: string;
}

interface PMSUserVerificationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUserSelected: (userId: string | null, fromPms: boolean) => void;
  // Dati per la ricerca utente
  searchData?: {
    name?: string;
    email?: string;
    dateOfBirth?: string;
    fiscalCode?: string;
  };
}

export function PMSUserVerificationDialog({
  isOpen,
  onOpenChange,
  onUserSelected,
  searchData,
}: PMSUserVerificationDialogProps) {
  const { t } = useTranslation();
  const [pmsUsers, setPmsUsers] = useState<PMSUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedIsFromPms, setSelectedIsFromPms] = useState<boolean>(false);
  const fetchPMSUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const params = new URLSearchParams();

      // Aggiungi i parametri di ricerca se disponibili
      if (searchData?.name) {
        params.append("name", searchData.name);
      }
      if (searchData?.email) {
        params.append("email", searchData.email);
      }
      if (searchData?.dateOfBirth) {
        params.append("dateOfBirth", searchData.dateOfBirth);
      }
      if (searchData?.fiscalCode) {
        params.append("fiscalCode", searchData.fiscalCode);
      }

      const response = await axios.get(
        `${environment.ELITE_BASE_URI}/configurationPmsApi/users?${params.toString()}`,
      );

      if (response.data) {
        const { data, isSuccess } = response.data;
        if (isSuccess && Array.isArray(data)) {
          setPmsUsers(data);
        } else {
          setPmsUsers([]);
        }
      }
    } catch (error) {
      setPmsUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Carica i dati quando la dialog si apre
  useEffect(() => {
    if (isOpen) {
      fetchPMSUsers();
      setSelectedUserId("");
      setSelectedIsFromPms(false);
    }
  }, [isOpen]);

  const handleCloseDialog = () => {
    onOpenChange(false);
    setSelectedUserId("");
  };

  const handleContinue = () => {
    onUserSelected(selectedUserId, selectedIsFromPms);
    handleCloseDialog();
  };

  const handleProceedAsNew = () => {
    onUserSelected(null, false);
    handleCloseDialog();
  };

  const canCloseDialog = () => {
    return selectedUserId || pmsUsers.length === 0;
  };

  return (
    <Dialog open={isOpen} onOpenChange={canCloseDialog}>
      <DialogContent showCloseButton={false} className="max-w-4xl ">
        <DialogHeader>
          <DialogTitle>{t("pmsUserVerification.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <RadioRows
            containerClassName="h-[400px] overflow-y-auto"
            value={selectedUserId}
            onValueChange={(value) => {
              setSelectedUserId(value);
              const matched = pmsUsers.find(
                (u) => (u.idPms ?? u.idGuest) === value,
              );
              setSelectedIsFromPms(matched?.idPms != null);
            }}
            className="my-4"
          >
            {pmsUsers &&
              pmsUsers?.map((user, index) => (
                <RadioRowsItem
                  value={user.idPms ?? user.idGuest}
                  variant="secondary"
                  key={index}
                  onClick={() => setSelectedIsFromPms(user.idPms != null)}
                >
                  <RadioRowsItemTitle className="flex gap-1 items-center">
                    {user?.birthCountry ? (
                      <span
                        className={`fi fi-${user?.birthCountry.toLowerCase()} rounded-xs`}
                      ></span>
                    ) : (
                      <Globe size={19} />
                    )}
                    {user.firstname} {user.lastname}
                  </RadioRowsItemTitle>
                  <RadioRowsItemDescription>
                    {user.idPms && (
                      <Badge
                        variant="secondary"
                        className="bg-green-500 text-white dark:bg-green-600"
                      >
                        <BadgeCheckIcon />
                        {t("pmsUserVerification.verified")}
                      </Badge>
                    )}
                  </RadioRowsItemDescription>
                  <RadioRowsItemDescription className="flex items-center gap-1">
                    <Badge variant="secondary">
                      <MailIcon />
                      {user?.email}
                    </Badge>
                    {user?.phone && (
                      <Badge variant="secondary">
                        <Phone />
                        {user?.phone}
                      </Badge>
                    )}
                  </RadioRowsItemDescription>
                  <RadioRowsItemDescription className="flex items-center gap-1">
                    {user?.residenceState && (
                      <Badge variant="secondary">
                        <span
                          className={`fi fi-${user.residenceState.toLowerCase()} rounded-xs`}
                        ></span>
                        {user.residenceState.toLowerCase()}
                      </Badge>
                    )}
                    {user?.birthDate && (
                      <Badge variant="secondary">
                        <CakeIcon />
                        {new Date(user?.birthDate).toLocaleDateString()}
                      </Badge>
                    )}
                  </RadioRowsItemDescription>
                </RadioRowsItem>
              ))}
          </RadioRows>
          {isLoadingUsers && (
            <Item variant={"muted"}>
              <ItemMedia>
                <Spinner />
              </ItemMedia>
              <ItemTitle>
                <LoadingItem
                  text={t("pmsUserVerification.loadingData")}
                  size="large"
                />
              </ItemTitle>
            </Item>
          )}
          {!isLoadingUsers && pmsUsers && pmsUsers?.length <= 0 && (
            <Item variant={"outline"}>
              <ItemMedia>
                <UserRoundSearch />
              </ItemMedia>
              <ItemTitle className="">
                {t("pmsUserVerification.noUserFound")}
              </ItemTitle>
              <ItemDescription>
                {t("pmsUserVerification.noUserDescription")}
              </ItemDescription>
            </Item>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleProceedAsNew}
            className="flex items-center gap-2"
          >
            <UserRoundPlus />
            {t("pmsUserVerification.proceedAsNew")}
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!selectedUserId || pmsUsers.length === 0}
            className="flex items-center gap-2"
          >
            <UserRoundCheck />
            {t("pmsUserVerification.continueWithSelected")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
